import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import CONFIG from '../../config.js';

const logo = new URL('../assets/logo.jpg', import.meta.url);

const NAV_ITEMS = [
  { label: '首页', path: '/' },
  { label: '赛事中心', path: '/events' },
  { label: '新闻资讯', path: '/news' },
  { label: '下载应用', path: '/download' },
  { label: '关于我们', path: '/about' },
];

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 72 }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 4 }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={logo}
                alt="中象在线"
                sx={{ height: 44, width: 44, borderRadius: 1, mr: 1.5 }}
              />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {CONFIG.appName}
              </Typography>
            </Box>

            {/* 桌面端导航 */}
            <Stack direction="row" spacing={1} sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    fontSize: '0.95rem',
                    '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>

            {/* 移动端菜单按钮 */}
            <IconButton
              sx={{ display: { md: 'none' }, ml: 'auto', color: 'text.primary' }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 移动端抽屉菜单 */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.path}
              selected={isActive(item.path)}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: isActive(item.path) ? 700 : 400,
                  color: isActive(item.path) ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
}

function Footer() {
  const navigate = useNavigate();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#1A1A1A',
        color: 'grey.400',
        pt: 8,
        pb: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
            gap: 4,
            mb: 6,
          }}
        >
          {/* 品牌区域 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logo}
                alt="中象在线"
                sx={{ height: 40, width: 40, borderRadius: 1, mr: 1.5 }}
              />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                {CONFIG.appName}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ lineHeight: 1.8, maxWidth: 300 }}>
              {CONFIG.appSlogan}。提供赛事报名、在线教学、人机对弈等功能，让每一位象棋爱好者都能享受学棋的乐趣。
            </Typography>
          </Box>

          {/* 快速链接 */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
              快速链接
            </Typography>
            {NAV_ITEMS.map((item) => (
              <Typography
                key={item.path}
                variant="body2"
                onClick={() => navigate(item.path)}
                sx={{
                  cursor: 'pointer',
                  mb: 1,
                  '&:hover': { color: 'primary.light' },
                  transition: 'color 0.2s',
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>

          {/* 下载应用 */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
              下载应用
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>微信小程序</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>Android 下载</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>iOS 下载</Typography>
          </Box>

          {/* 联系我们 */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
              联系我们
            </Typography>
            {CONFIG.contact.email && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                邮箱：{CONFIG.contact.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'grey.600' }}>
            &copy; {new Date().getFullYear()} {CONFIG.appName}. All rights reserved.
          </Typography>
          {CONFIG.icp && (
            <Typography
              component="a"
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                color: 'grey.600',
                textDecoration: 'none',
                '&:hover': { color: 'grey.400' },
              }}
            >
              {CONFIG.icp}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default function Layout() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>
      <Footer />
    </Box>
  );
}
